/**
 * Scrolls the nearest vertically scrolling ancestor (or the page) just enough
 * to show `element` — its top when it is taller than the view. Unlike
 * `scrollIntoView`, it never scrolls sideways and never moves ancestors that
 * only clip (`overflow: hidden`).
 */
export function revealVertically(
  element: Element,
  behavior: ScrollBehavior = 'smooth'
) {
  const scroller = verticalScrollParent(element);
  const view = scroller
    ? {
        top: scroller.getBoundingClientRect().top + scroller.clientTop,
        height: scroller.clientHeight
      }
    : { top: 0, height: window.innerHeight };
  const rect = element.getBoundingClientRect();
  const below = rect.bottom - (view.top + view.height);
  const above = rect.top - view.top;
  const delta = below > 0 ? Math.min(below, above) : Math.min(above, 0);
  if (delta === 0) return;
  (scroller ?? window).scrollBy({ top: delta, behavior });
}

/** The nearest ancestor that scrolls vertically and has something to scroll. */
export function verticalScrollParent(element: Element): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    const scrolls = overflowY === 'auto' || overflowY === 'scroll';
    if (scrolls && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}
